/**
 * マッチング設定管理用の API hooks
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * 通知先メールアドレスの型
 */
export type NotificationRecipient = {
  email: string;
  userId: string | null;
  displayName: string | null;
};

/**
 * マッチング設定の型
 */
export interface MatchingCondition {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  matchingMode: string;
  minMargin: number;
  maxEmailAgeDays: number | null;
  maxDepth: number | null;
  minTotalScore: number;
  slackNotificationEnabled: boolean;
  emailNotificationEnabled: boolean;
  emailNotificationRecipients: NotificationRecipient[];
  createdAt: string;
  updatedAt: string;
}

/**
 * マッチング設定一覧レスポンスの型
 */
interface MatchingConditionListResponse {
  items: MatchingCondition[];
  total: number;
}

/**
 * マッチング設定作成リクエストの型
 */
export interface CreateMatchingConditionInput {
  name: string;
  description?: string | null;
  matchingMode: string;
  minMargin: number;
  maxEmailAgeDays?: number | null;
  maxDepth?: number | null;
  minTotalScore: number;
  slackNotificationEnabled?: boolean;
  emailNotificationEnabled?: boolean;
  emailNotificationRecipients?: NotificationRecipient[];
}

/**
 * マッチング設定更新リクエストの型
 */
export interface UpdateMatchingConditionInput {
  name: string;
  description?: string | null;
  matchingMode: string;
  minMargin: number;
  maxEmailAgeDays?: number | null;
  maxDepth?: number | null;
  minTotalScore: number;
  slackNotificationEnabled?: boolean;
  emailNotificationEnabled?: boolean;
  emailNotificationRecipients?: NotificationRecipient[];
}

/**
 * マッチング設定一覧を取得する hook
 * @return {object} settings, total, loading, error, refetch
 */
export function useMatchingConditions(): {
  settings: MatchingCondition[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [settings, setSettings] = useState<MatchingCondition[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<MatchingConditionListResponse>(
        "/matching/conditions",
      );
      setSettings(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settings, total, loading, error, refetch: fetchSettings };
}

/**
 * マッチング設定詳細を取得する hook
 * @param {string | undefined} settingId 設定ID
 * @return {object} setting, loading, error, refetch
 */
export function useMatchingCondition(settingId: string | undefined): {
  setting: MatchingCondition | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [setting, setSetting] = useState<MatchingCondition | null>(null);
  const [loading, setLoading] = useState(!!settingId);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSetting = useCallback(async () => {
    if (!settingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<MatchingCondition>(
        `/matching/conditions/${settingId}`,
      );
      setSetting(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [settingId]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  return { setting, loading, error, refetch: fetchSetting };
}

/**
 * マッチング設定を作成する hook
 * @return {object} createSetting, loading, error
 */
export function useCreateMatchingCondition(): {
  createSetting: (
    input: CreateMatchingConditionInput,
  ) => Promise<MatchingCondition>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createSetting = useCallback(
    async (input: CreateMatchingConditionInput): Promise<MatchingCondition> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<MatchingCondition>(
          "/matching/conditions",
          input,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createSetting, loading, error };
}

/**
 * マッチング設定を更新する hook
 * @return {object} updateSetting, loading, error
 */
export function useUpdateMatchingCondition(): {
  updateSetting: (
    settingId: string,
    input: UpdateMatchingConditionInput,
  ) => Promise<MatchingCondition>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateSetting = useCallback(
    async (
      settingId: string,
      input: UpdateMatchingConditionInput,
    ): Promise<MatchingCondition> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<MatchingCondition>(
          `/matching/conditions/${settingId}`,
          input,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateSetting, loading, error };
}

/**
 * マッチング設定を削除する hook
 * @return {object} deleteSetting, loading, error
 */
export function useDeleteMatchingCondition(): {
  deleteSetting: (settingId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deleteSetting = useCallback(
    async (settingId: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        await restClient.delete(`/matching/conditions/${settingId}`);
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { deleteSetting, loading, error };
}

/**
 * マッチング設定をアクティブ化する hook
 * @return {object} activateSetting, loading, error
 */
export function useActivateMatchingCondition(): {
  activateSetting: (settingId: string) => Promise<MatchingCondition>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const activateSetting = useCallback(
    async (settingId: string): Promise<MatchingCondition> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.patch<MatchingCondition>(
          `/matching/conditions/${settingId}/activate`,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { activateSetting, loading, error };
}

/**
 * マッチング設定を非アクティブ化する hook
 * @return {object} deactivateSetting, loading, error
 */
export function useDeactivateMatchingCondition(): {
  deactivateSetting: (settingId: string) => Promise<MatchingCondition>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deactivateSetting = useCallback(
    async (settingId: string): Promise<MatchingCondition> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.patch<MatchingCondition>(
          `/matching/conditions/${settingId}/deactivate`,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { deactivateSetting, loading, error };
}

/**
 * テナントマッチング設定の型
 */
export type TenantMatchingSettings = {
  autoMatchingEnabled: boolean;
};

/**
 * テナントの自動マッチング設定を取得する hook
 * @return {object} settings, loading, error, refetch
 */
export function useTenantMatchingSettings(): {
  settings: TenantMatchingSettings | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [settings, setSettings] = useState<TenantMatchingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<TenantMatchingSettings>(
        "/matching/conditions/tenant-matching",
      );
      setSettings(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settings, loading, error, refetch: fetchSettings };
}

/**
 * テナントの自動マッチング設定を更新する hook
 * @return {object} updateSettings, loading, error
 */
export function useUpdateTenantMatchingSettings(): {
  updateSettings: (
    autoMatchingEnabled: boolean,
  ) => Promise<TenantMatchingSettings>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateSettings = useCallback(
    async (autoMatchingEnabled: boolean): Promise<TenantMatchingSettings> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<TenantMatchingSettings>(
          "/matching/conditions/tenant-matching",
          { autoMatchingEnabled },
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateSettings, loading, error };
}
